import type { FC, ReactNode } from 'react'
import type { OrderStatus } from '@/types'

import { CircleAlert, CircleCheck, Info, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

/* Shared form and status pieces for the account, checkout and admin pages. */

export const inputClassName = 'h-12 w-full rounded-2xl border border-cream/15 bg-espresso/60 px-4 text-base text-cream outline-none transition-[border-color,box-shadow] placeholder:text-cream/35 focus-visible:border-crema focus-visible:ring-2 focus-visible:ring-crema/30 aria-[invalid=true]:border-red-400/70 disabled:opacity-60'

export const textareaClassName = 'min-h-28 w-full rounded-2xl border border-cream/15 bg-espresso/60 px-4 py-3 text-base text-cream outline-none transition-[border-color,box-shadow] placeholder:text-cream/35 focus-visible:border-crema focus-visible:ring-2 focus-visible:ring-crema/30'

const buttonBase = 'inline-flex cursor-pointer items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crema disabled:cursor-not-allowed disabled:opacity-60'

export const buttonPrimary = cn(buttonBase, 'h-12 bg-cream text-espresso hover:bg-white')

export const buttonAccent = cn(buttonBase, 'h-12 bg-crema text-espresso hover:brightness-105')

export const buttonSecondary = cn(buttonBase, 'h-11 border border-cream/15 text-cream hover:bg-cream/10')

export const buttonDanger = cn(buttonBase, 'h-11 border border-red-400/30 text-red-200 hover:bg-red-500/10')

export const cardClassName = 'rounded-[1.5rem] border border-cream/10 bg-roast p-5 sm:p-6'

interface FieldProps {
    label: string
    htmlFor: string
    hint?: ReactNode
    optional?: boolean
    children: ReactNode
    className?: string
}

export const Field: FC<FieldProps> = ({ label, htmlFor, hint, optional, children, className }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={htmlFor} className='text-sm font-medium text-cream/85'>
                {label}
                {optional && <span className='font-normal text-cream/45'> (opsional)</span>}
            </label>

            <div className='mt-1.5'>{children}</div>

            {hint && <p className='mt-1.5 text-xs leading-relaxed text-cream/50'>{hint}</p>}
        </div>
    )
}

interface NoticeProps {
    tone?: 'error' | 'success' | 'info'
    children: ReactNode
    className?: string
}

export const Notice: FC<NoticeProps> = ({ tone = 'info', children, className }): ReactNode => {
    const Icon = tone === 'error' ? CircleAlert : tone === 'success' ? CircleCheck : Info

    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn(
                'flex items-start gap-3 rounded-2xl px-4 py-3 text-sm leading-relaxed',
                tone === 'error' && 'bg-red-500/10 text-red-100',
                tone === 'success' && 'bg-emerald-500/10 text-emerald-100',
                tone === 'info' && 'bg-cream/[0.06] text-cream/80',
                className
            )}
        >
            <Icon aria-hidden className='mt-0.5 size-4 shrink-0' />
            <div className='min-w-0'>{children}</div>
        </div>
    )
}

export const Spinner: FC = (): ReactNode => <LoaderCircle aria-hidden className='size-4 animate-spin' />

const STATUS_STYLES: Record<OrderStatus, string> = {
    'menunggu pembayaran': 'bg-amber-400/15 text-amber-200',
    dibayar: 'bg-emerald-400/15 text-emerald-200',
    dibatalkan: 'bg-red-400/15 text-red-200',
    kedaluwarsa: 'bg-cream/10 text-cream/60'
}

export const STATUS_LABELS: Record<OrderStatus, string> = {
    'menunggu pembayaran': 'Menunggu pembayaran',
    dibayar: 'Dibayar',
    dibatalkan: 'Dibatalkan',
    kedaluwarsa: 'Kedaluwarsa'
}

export const StatusBadge: FC<{ status: OrderStatus }> = ({ status }): ReactNode => {
    return (
        <span className={cn('inline-flex w-fit items-center rounded-full px-2.5 py-1 text-xs font-semibold', STATUS_STYLES[status])}>
            {STATUS_LABELS[status]}
        </span>
    )
}

/* Formats an ISO timestamp for Jakarta time, the same on server and browser. */
export const formatDate = (value: string | null, withTime = true): string => {
    if (!value) return '-'

    return new Intl.DateTimeFormat('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        timeZone: 'Asia/Jakarta'
    }).format(new Date(value))
}

/* Only same-site paths are accepted as a place to return to after signing in. */
export const safeNext = (value: unknown, fallback = '/akun'): string => {
    return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') ? value : fallback
}
